import React, { useState, useEffect } from "react";
import { endPoints } from "../../../constant/Environment";
import { addData, getAllData } from "../../../Utility/API";
import { Modal, Form, Button } from "react-bootstrap";
import moment from "moment";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Loader from "../../../components/Loader";
import { ErrorToast, SuccesToast } from "../../../components/Toast/message";

/**
 * Grant Free Round — a single admin action backed by the common backend endpoint
 * POST /freeround/grant. The admin picks a provider and the form shows only the fields
 * that provider needs; the payload mirrors the FreeCOIN Postman collection exactly.
 *
 *   netgaming   -> netgaming-promotion/grant-bonus
 *   blazegaming -> blazegaming/createFreeRound
 *   kagaming    -> kagaming-promotion/create
 *   evoplay     -> evoplay-games/register-bonus
 */

const PROVIDERS = [
  { value: "netgaming", label: "NetGaming (grant bonus)" },
  { value: "blazegaming", label: "BlazeGaming (create free round)" },
  { value: "kagaming", label: "KA Gaming (create)" },
  { value: "evoplay", label: "Evoplay (register bonus)" },
];

const KA_GAMING_FREE_COUNTS = [5, 10, 50, 100];

// A unique, random 32-character transaction id for a BlazeGaming free-round grant.
// Generated per grant and sent in the payload; not shown in the UI.
const randomTxnId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, ""); // 32 hex chars
  }
  let s = "";
  while (s.length < 32) s += Math.random().toString(16).slice(2);
  return s.slice(0, 32);
};

// The current user's session id (stored JSON-quoted, like the axios instances read it).
const getUserSessionId = () => {
  const raw = sessionStorage.getItem("session");
  if (!raw) return "";
  try {
    return JSON.parse(raw);
  } catch (e) {
    return raw.replace(/"/g, "");
  }
};

// Splits a comma/space separated string into a trimmed, non-empty array.
const splitList = (value) =>
  (value || "")
    .split(",")
    .map((v) => v.trim())
    .filter((v) => v.length > 0);

const FreeRoundGrant = ({ userId, playerName: playerNameProp, currency: currencyProp }) => {
  const [show, setShow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [provider, setProvider] = useState("netgaming");

  // Games for the selected provider, loaded from getGamesByProvider.
  const [gameOptions, setGameOptions] = useState([]);
  const [loadingGames, setLoadingGames] = useState(false);

  // Selectable bet values for the chosen game+currency (BlazeGaming), from /gamebets.
  const [betOptions, setBetOptions] = useState([]);
  const [loadingBets, setLoadingBets] = useState(false);

  // Shared
  const [playerId, setPlayerId] = useState(userId ? String(userId) : "");
  const [currency, setCurrency] = useState(currencyProp || "USD");
  const [gameId, setGameId] = useState("");
  const [endDate, setEndDate] = useState("");

  // NetGaming (uses the shared gameId; the API wraps it in a single-element list)
  const [campaignId, setCampaignId] = useState("");
  const [coinValueLevel, setCoinValueLevel] = useState("");
  const [numRounds, setNumRounds] = useState("");
  const [startDate, setStartDate] = useState("");

  // BlazeGaming
  const [playerName, setPlayerName] = useState(playerNameProp || "");
  const [countryCode, setCountryCode] = useState("US");
  const [languageCode, setLanguageCode] = useState("EN");
  const [sessionKey, setSessionKey] = useState(getUserSessionId());
  const [totalBetValue, setTotalBetValue] = useState("");
  const [roundOptions, setRoundOptions] = useState("");

  // KA Gaming
  const [betLevel, setBetLevel] = useState("");

  const resetForm = () => {
    setProvider("netgaming");
    setPlayerId(userId ? String(userId) : "");
    setCurrency(currencyProp || "USD");
    setGameId("");
    setEndDate("");
    setCampaignId("");
    setCoinValueLevel("");
    setNumRounds("");
    setStartDate("");
    setPlayerName(playerNameProp || "");
    setCountryCode("US");
    setLanguageCode("EN");
    setSessionKey(getUserSessionId());
    setTotalBetValue("");
    setRoundOptions("");
    setBetLevel("");
  };

  const fetchHistory = async () => {
    if (!userId) return;
    const url = `${endPoints.apiBaseUrl}${endPoints.api.FREE_ROUND_HISTORY}${userId}`;
    try {
      const response = await getAllData(url);
      if (response?.status === "success") {
        setRows(response?.data || []);
      }
    } catch (error) {
      console.error("Error fetching free round history:", error);
    }
  };

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Loads the games belonging to the selected provider for the Game dropdown(s).
  const fetchGamesByProvider = async (providerName) => {
    setLoadingGames(true);
    setGameOptions([]);
    try {
      const url = `${endPoints.api.FREE_GAMES_BY_PROVIDER}${encodeURIComponent(providerName)}`;
      const response = await getAllData(url);
      // gamesbyprovider returns a bare list; other list endpoints wrap it in {status,data}.
      const list = Array.isArray(response)
        ? response
        : response?.status === "success"
        ? response?.data || []
        : response?.data || [];
      setGameOptions(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Error fetching games by provider:", error);
      setGameOptions([]);
    } finally {
      setLoadingGames(false);
    }
  };

  // Refetch games whenever the modal is open and the provider changes.
  useEffect(() => {
    if (show && provider) {
      fetchGamesByProvider(provider);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, provider]);

  // Selectable bet values for a BlazeGaming game in the player's currency.
  const fetchGameBets = async (gid, curr) => {
    setLoadingBets(true);
    setBetOptions([]);
    try {
      const url = `${endPoints.api.GAME_BETS}${encodeURIComponent(gid)}/${encodeURIComponent(curr)}`;
      const response = await getAllData(url);
      const values = response?.status === "success" ? response?.data?.bet_values : null;
      setBetOptions(Array.isArray(values) ? values : []);
    } catch (error) {
      console.error("Error fetching game bets:", error);
      setBetOptions([]);
    } finally {
      setLoadingBets(false);
    }
  };

  // BlazeGaming: load the game's bet values (for the Total Bet Value dropdown)
  // whenever the selected game or currency changes.
  useEffect(() => {
    setTotalBetValue("");
    if (show && (provider === "blazegaming" || provider === "kagaming" || provider === "evoplay") && gameId && currency) {
      fetchGameBets(gameId, currency);
    } else {
      setBetOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, provider, gameId, currency]);

  const handleOpen = () => {
    resetForm();
    setShow(true);
  };

  // Switching provider clears any game selection made for the previous provider.
  const handleProviderChange = (value) => {
    setProvider(value);
    setGameId("");
  };

  const handleClose = () => setShow(false);

  // Builds the provider-specific payload and validates the fields that provider requires.
  const buildPayload = () => {
    if (!playerId) {
      ErrorToast("Player ID is required");
      return null;
    }

    if (provider === "netgaming") {
      if (!campaignId) {
        ErrorToast("Campaign ID is required");
        return null;
      }
      if (!gameId || Number.isNaN(Number(gameId))) {
        ErrorToast("Please select a (numeric) game");
        return null;
      }
      if (!numRounds) {
        ErrorToast("Number of rounds is required");
        return null;
      }
      return {
        provider,
        campaignId,
        gameIds: [Number(gameId)],
        coinValueLevel: coinValueLevel ? Number(coinValueLevel) : null,
        numRounds: Number(numRounds),
        playerIds: [playerId],
        currencies: [currency],
        startDate: startDate ? moment(startDate).format("YYYY-MM-DD HH:mm:ss") : null,
        endDate: endDate ? moment(endDate).format("YYYY-MM-DD HH:mm:ss") : null,
      };
    }

    if (provider === "blazegaming") {
      if (!gameId) {
        ErrorToast("Game ID is required");
        return null;
      }
      if (!totalBetValue) {
        ErrorToast("Total bet value is required");
        return null;
      }
      return {
        provider,
        timeStamp: moment().format("YYYY-MM-DDTHH:mm:ss"),
        playerId,
        playerName,
        countryCode,
        currency,
        languageCode,
        gameId,
        sessionKey,
        txnId: randomTxnId(),
        totalBetValue: Number(totalBetValue),
        roundOptions: splitList(roundOptions).map((v) => Number(v)),
      };
    }

    // kagaming and evoplay share these fields: player, game, spin count, bet level, expiry.
    if (!gameId) {
      ErrorToast("Game ID is required");
      return null;
    }
    if (!numRounds) {
      ErrorToast("Number of spins is required");
      return null;
    }
    // Evoplay documents the bet as optional but its API fails without one.
    if (provider === "evoplay" && !betLevel) {
      ErrorToast("Bet level is required for Evoplay");
      return null;
    }
    return {
      provider,
      currency,
      playerId,
      numRounds: Number(numRounds),
      betLevel: betLevel ? Number(betLevel) : null,
      endDate: endDate ? moment(endDate).format("YYYY-MM-DD HH:mm:ss") : null,
      gameId,
    };
  };

  const handleSubmit = () => {
    const payload = buildPayload();
    if (!payload) return;

    setIsLoading(true);
    addData(endPoints.api.FREE_ROUND_GRANT, payload)
      .then((response) => {
        setIsLoading(false);
        if (response?.data?.status === "success") {
          SuccesToast(response?.data?.message || "Free round granted successfully");
          fetchHistory();
          handleClose();
        } else {
          ErrorToast(response?.data?.message || "Failed to grant free round");
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error("Grant free round error:", error);
        ErrorToast("Failed to grant free round");
      });
  };

  return (
    <>
      {isLoading && <Loader spinner={true} visible={isLoading} />}
      <div className="inner-space12">
        <div className="row">
          <div
            className="col-md-12"
            style={{
              display: "flex",
              justifyContent: "space-between",
              borderBottom: "1px solid #444",
            }}
          >
            <h3 className="mb-2" style={{ fontFamily: "ArticulatCF-Bold" }}>
              Free Round
            </h3>
            <Button
              variant="primary"
              className="mrglft10"
              style={{ height: "fit-content" }}
              onClick={handleOpen}
            >
              Grant Free Round
            </Button>
          </div>
        </div>
      </div>

      <div className="container-fluid table-custom">
        <div className="row" style={{ padding: "5px 0px 20px 0px" }}>
          <div className="col-md-12">
            <TableContainer component={Paper}>
              <Table
                className="c_table"
                size="small"
                aria-label="Free round history table"
                stickyHeader
                sx={{ minWidth: 650 }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Provider</TableCell>
                    <TableCell align="center">Game</TableCell>
                    <TableCell align="center">Rounds</TableCell>
                    <TableCell align="center">Currency</TableCell>
                    <TableCell align="center">Bet Level</TableCell>
                    <TableCell align="center">Promo ID</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Granted By</TableCell>
                    <TableCell align="center">Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        No free rounds granted yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row, index) => (
                      <TableRow key={row.id || index}>
                        <TableCell align="center">{row?.provider}</TableCell>
                        <TableCell align="center">{row?.gameRef || "—"}</TableCell>
                        <TableCell align="center">{row?.numRounds ?? "—"}</TableCell>
                        <TableCell align="center">{row?.currency || "—"}</TableCell>
                        <TableCell align="center">{row?.betLevel ?? "—"}</TableCell>
                        <TableCell align="center">{row?.promoFreespinId || "—"}</TableCell>
                        <TableCell align="center">{row?.status}</TableCell>
                        <TableCell align="center">{row?.grantedBy ?? "—"}</TableCell>
                        <TableCell align="center">
                          {row?.createdAt
                            ? moment(row.createdAt).format("MM/DD/YYYY hh:mm")
                            : "—"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </div>
      </div>

      <Modal
        className="modal-md modal-dialog-centered"
        show={show}
        onHide={handleClose}
        size="lg"
        centered
        dialogClassName="custom-modal-center"
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">Grant Free Round</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Provider</Form.Label>
                <Form.Select
                  value={provider}
                  onChange={(e) => handleProviderChange(e.target.value)}
                >
                  {PROVIDERS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Player ID</Form.Label>
                <Form.Control value={playerId} readOnly />
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Currency</Form.Label>
                <Form.Control value={currency} readOnly />
              </Form.Group>
            </div>

            {/* ---------- NetGaming ---------- */}
            {provider === "netgaming" && (
              <>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Campaign ID</Form.Label>
                    <Form.Control
                      value={campaignId}
                      onChange={(e) => setCampaignId(e.target.value)}
                      placeholder="cpmp12"
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Game {loadingGames ? "(loading…)" : ""}</Form.Label>
                    <Form.Select
                      value={gameId}
                      onChange={(e) => setGameId(e.target.value)}
                    >
                      <option value="">Select a game</option>
                      {gameOptions.map((g) => (
                        <option key={g.game_id} value={g.game_id}>
                          {g.game_name} ({g.game_id})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Coin Value Level</Form.Label>
                    <Form.Control
                      type="number"
                      value={coinValueLevel}
                      onChange={(e) => setCoinValueLevel(e.target.value)}
                      placeholder="1"
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Number of Rounds</Form.Label>
                    <Form.Control
                      type="number"
                      value={numRounds}
                      onChange={(e) => setNumRounds(e.target.value)}
                      placeholder="10"
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Form.Group>
                </div>
              </>
            )}

            {/* ---------- BlazeGaming ---------- */}
            {provider === "blazegaming" && (
              <>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Game {loadingGames ? "(loading…)" : ""}</Form.Label>
                    <Form.Select
                      value={gameId}
                      onChange={(e) => setGameId(e.target.value)}
                    >
                      <option value="">Select a game</option>
                      {gameOptions.map((g) => (
                        <option key={g.game_id} value={g.game_id}>
                          {g.game_name} ({g.game_id})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Player Name</Form.Label>
                    <Form.Control value={playerName} readOnly />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Country Code</Form.Label>
                    <Form.Control value={countryCode} readOnly />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Language Code</Form.Label>
                    <Form.Control value={languageCode} readOnly />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Session Key</Form.Label>
                    <Form.Control value={sessionKey} readOnly />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Total Bet Value {loadingBets ? "(loading…)" : ""}</Form.Label>
                    <Form.Select
                      value={totalBetValue}
                      onChange={(e) => setTotalBetValue(e.target.value)}
                      disabled={!gameId}
                    >
                      <option value="">
                        {!gameId
                          ? "Select a game first"
                          : betOptions.length === 0
                          ? "No bet values for this game/currency"
                          : "Select bet value"}
                      </option>
                      {betOptions.map((b) => (
                        <option key={b} value={b}>
                          {b}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Round Options (comma separated)</Form.Label>
                    <Form.Control
                      value={roundOptions}
                      onChange={(e) => setRoundOptions(e.target.value)}
                      placeholder="1, 2"
                    />
                  </Form.Group>
                </div>
              </>
            )}

            {/* ---------- KA Gaming ---------- */}
            {(provider === "kagaming" || provider === "evoplay") && (
              <>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Game {loadingGames ? "(loading…)" : ""}</Form.Label>
                    <Form.Select
                      value={gameId}
                      onChange={(e) => setGameId(e.target.value)}
                    >
                      <option value="">Select a game</option>
                      {gameOptions.map((g) => (
                        <option key={g.game_id} value={g.game_id}>
                          {g.game_name} ({g.game_id})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Number of Spins</Form.Label>
                    <Form.Select
                      value={numRounds}
                      onChange={(e) => setNumRounds(e.target.value)}
                    >
                      <option value="">Select spins</option>
                      {KA_GAMING_FREE_COUNTS.map((count) => (
                        <option key={count} value={count}>
                          {count}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Total Bet Value {loadingBets ? "(loading…)" : ""}</Form.Label>
                    <Form.Select
                      value={totalBetValue}
                      onChange={(e) => setTotalBetValue(e.target.value)}
                      disabled={!gameId}
                    >
                      <option value="">
                        {!gameId
                          ? "Select a game first"
                          : betOptions.length === 0
                          ? "No bet values for this game/currency"
                          : "Select bet value"}
                      </option>
                      {betOptions.map((b) => (
                        <option key={b} value={b}>
                          {b/100}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Bet Level</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="any"
                      placeholder="Stake per free round, e.g. 10"
                      value={betLevel}
                      onChange={(e) => setBetLevel(e.target.value)}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Form.Group>
                </div>
              </>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={handleSubmit}
            style={{ background: "#04631e", borderColor: "#04631e" }}
          >
            Grant Free Round
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default FreeRoundGrant;
